// ./src/components/features/VolunteerSlots/VolunteerLocationFields/VolunteerLocationFields.tsx
//
// WHERE a volunteer slot happens. Four modes (+ None = inherit the event venue):
//   - Venue       → pick a saved venue/vendor (with in-place create)
//   - POI         → pick one of the event's points of interest
//   - Address     → search a one-off place / address (captures address + coords)
//   - Coordinates → raw lat/lng + an optional coverage radius (m/ft)
// Emits the whole location value on every change.

import React, { useEffect, useMemo, useState } from "react";
import { Stack, SegmentedControl, Loader, Input, Text } from "@mantine/core";

import { VenuesProvider, useVenuesContext } from "@/contexts";
import {
  Venue,
  VENUE_KIND,
  PointOfInterest,
  PointOfInterestService,
  VolunteerRadiusUnit,
  WHOLE_SET,
} from "@/utils/api";
import { AppIcon, AppButton, PlaceAutocompleteInput, SelectField } from "@/components/generic";
import { PlaceData } from "@/hooks/useGoogleMapsAutocomplete";
import { DRAWER_COMBOBOX_PROPS, formatPlaceLineFromPlace } from "@/utils";
import { VenueEditDrawer } from "@/components/features/Venues";
import { useCreateInPlace } from "@/hooks/useCreateInPlace";
import { CREATE_IN_OWN_ACCOUNT } from "@/utils/createInPlace";

import { CoordinatePickerDrawer } from "../CoordinatePickerDrawer/CoordinatePickerDrawer";

const NONE = "none";
const LOCATION_PLACE_TYPES = ["establishment", "geocode"];

export interface VolunteerLocationValue {
  kind: string | null;
  venueId: string | null;
  pointOfInterestId: string | null;
  address: string;
  latitude: number | null;
  longitude: number | null;
  radius: number | null;
  radiusUnit: VolunteerRadiusUnit;
  /** Denormalized human label for the chosen venue / POI / place (saved as locationLabel). */
  label: string | null;
}

export const EMPTY_VOLUNTEER_LOCATION: VolunteerLocationValue = {
  kind: null,
  venueId: null,
  pointOfInterestId: null,
  address: "",
  latitude: null,
  longitude: null,
  radius: null,
  radiusUnit: "m",
  label: null,
};

export interface VolunteerLocationFieldsProps {
  value: VolunteerLocationValue;
  onChange: (next: VolunteerLocationValue) => void;
  eventId: string;
  disabled?: boolean;
  fallbackVenueLabel?: string | null;
  error?: React.ReactNode;
}

const VolunteerLocationFieldsInner: React.FC<VolunteerLocationFieldsProps> = ({
  value,
  onChange,
  eventId,
  disabled,
  fallbackVenueLabel,
  error,
}) => {
  const { venues, loading: venuesLoading } = useVenuesContext();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // #142 — may this person add one here? (never for a crew member: it would land in their OWN account).
  const canCreate = useCreateInPlace(CREATE_IN_OWN_ACCOUNT);
  const [mapOpen, setMapOpen] = useState(false);

  const [pois, setPois] = useState<PointOfInterest[]>([]);
  const [poisLoading, setPoisLoading] = useState(false);

  const kind = value.kind ?? null;

  useEffect(() => {
    let cancelled = false;

    if (kind === "poi" && pois.length === 0 && eventId) {
      setPoisLoading(true);
      PointOfInterestService.listAll({ eventId: [eventId] }, [{ name: "name", order: "ASC" }]).then(
        (result) => {
          if (cancelled) {
            return;
          }
          if (result.didSucceed()) {
            setPois((result.data as PointOfInterest[]) || []);
          }
          setPoisLoading(false);
        }
      );
    }

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, eventId]);

  const venueData = useMemo(() => {
    const items = venues
      .filter((v) => !!v.id)
      .map((v) => ({
        value: v.id as string,
        label: `${v.name || "Untitled"}${v.kind === VENUE_KIND.VENDOR ? " · Vendor" : ""}`,
      }));

    if (value.venueId && !items.some((o) => o.value === value.venueId)) {
      items.unshift({ value: value.venueId, label: fallbackVenueLabel || "Selected venue" });
    }

    return items;
  }, [venues, value.venueId, fallbackVenueLabel]);

  const poiData = useMemo(
    () => pois.filter((p) => !!p.id).map((p) => ({ value: p.id as string, label: p.name || "Untitled" })),
    [pois]
  );

  const setKind = (next: string) => {
    if (next === NONE) {
      onChange({ ...EMPTY_VOLUNTEER_LOCATION, radiusUnit: value.radiusUnit });
      return;
    }
    // Switching modes clears the denormalized label — it's re-derived from the new pick.
    onChange({ ...value, kind: next, label: null });
  };

  const venueNameById = (id: string | null): string | null => {
    if (!id) {
      return null;
    }
    return venues.find((v) => v.id === id)?.name ?? value.label ?? fallbackVenueLabel ?? null;
  };

  const poiNameById = (id: string | null): string | null => {
    if (!id) {
      return null;
    }
    return pois.find((p) => p.id === id)?.name ?? value.label ?? null;
  };

  const handleVenueCreated = (venue: Venue) => {
    setDrawerOpen(false);
    if (venue.id) {
      onChange({ ...value, venueId: venue.id, label: venue.name ?? null });
    }
  };

  const handleAddress = (address1: string, place: PlaceData | null) => {
    if (place) {
      const line = formatPlaceLineFromPlace(place) || address1;
      onChange({
        ...value,
        address: line,
        latitude: typeof place.address?.latitude === "number" ? place.address.latitude : value.latitude,
        longitude: typeof place.address?.longitude === "number" ? place.address.longitude : value.longitude,
        label: line || null,
      });
      return;
    }
    onChange({ ...value, address: address1, latitude: null, longitude: null, label: address1 || null });
  };

  return (
    <Input.Wrapper label="Where" description="Where volunteers report for this slot." error={error}>
      <Stack gap="xs" mt={4}>
        <SegmentedControl
          fullWidth
          value={kind ?? NONE}
          onChange={setKind}
          disabled={disabled}
          data={[
            { value: NONE, label: "Event venue" },
            { value: "venue", label: "Venue" },
            { value: "poi", label: "POI" },
            { value: "address", label: "Address" },
            { value: "coordinates", label: "Coordinates" },
          ]}
        />

        {kind === "venue" ? (
          <SelectField
            placeholder={venuesLoading ? "Loading…" : "Pick a venue or vendor"}
            data={venueData}
            value={value.venueId}
            onChange={(_n, id) => onChange({ ...value, venueId: id, label: venueNameById(id) })}
            createLabel={canCreate ? "Add a new venue / vendor" : undefined}
            onCreate={canCreate ? () => setDrawerOpen(true) : undefined}
            searchable
            clearable
            disabled={disabled}
            nothingFoundMessage="No venues yet — add one"
            leftSection={<AppIcon iconName="MapPin" size={16} />}
            rightSection={venuesLoading ? <Loader size="md" /> : undefined}
            comboboxProps={DRAWER_COMBOBOX_PROPS}
            size="md"
          />
        ) : null}

        {kind === "poi" ? (
          <SelectField
            placeholder={poisLoading ? "Loading…" : "Pick a point of interest"}
            data={poiData}
            value={value.pointOfInterestId}
            onChange={(_n, id) => onChange({ ...value, pointOfInterestId: id, label: poiNameById(id) })}
            searchable
            clearable
            disabled={disabled}
            nothingFoundMessage="No POIs for this event yet"
            leftSection={<AppIcon iconName="MapPin" size={16} />}
            rightSection={poisLoading ? <Loader size="md" /> : undefined}
            comboboxProps={DRAWER_COMBOBOX_PROPS}
            size="md"
          />
        ) : null}

        {kind === "address" ? (
          <PlaceAutocompleteInput
            label="Address"
            placeholder="Search a place, corner or address"
            value={value.address}
            onChange={handleAddress}
            placeTypes={LOCATION_PLACE_TYPES}
            disabled={disabled}
            withinPortal
            size="md"
          />
        ) : null}

        {kind === "coordinates" ? (
          <Stack gap="xs">
            <AppButton
              variant="secondary"
              size="md"
              iconName="MapPin"
              onClick={() => setMapOpen(true)}
              disabled={disabled}
              fullWidth
            >
              {value.latitude != null && value.longitude != null ? "Change location on map" : "Pick location on map"}
            </AppButton>
            <Text size="sm" c="dimmed">
              {value.latitude != null && value.longitude != null
                ? `${value.latitude.toFixed(5)}, ${value.longitude.toFixed(5)}${value.radius ? ` · ${value.radius} ${value.radiusUnit} radius` : ""}`
                : "Search an address or drop a pin, then set an optional coverage radius."}
            </Text>
          </Stack>
        ) : null}

        <VenueEditDrawer opened={drawerOpen} onClose={() => setDrawerOpen(false)} kind={VENUE_KIND.VENUE} onCreated={handleVenueCreated} />

        <CoordinatePickerDrawer
          opened={mapOpen}
          onClose={() => setMapOpen(false)}
          value={{ latitude: value.latitude, longitude: value.longitude, radius: value.radius, radiusUnit: value.radiusUnit }}
          onSave={(v) =>
            onChange({ ...value, latitude: v.latitude, longitude: v.longitude, radius: v.radius, radiusUnit: v.radiusUnit as VolunteerRadiusUnit })
          }
        />
      </Stack>
    </Input.Wrapper>
  );
};

export const VolunteerLocationFields: React.FC<VolunteerLocationFieldsProps> = (props) => {
  return (
    <VenuesProvider pageSize={WHOLE_SET}>
      <VolunteerLocationFieldsInner {...props} />
    </VenuesProvider>
  );
};

export default VolunteerLocationFields;
